import { Router, ScreenBase } from "@frui.ts/screens";
import { czechID } from "config";
import differenceInDays from "date-fns/differenceInDays";
import { interfaces } from "inversify";
import { action, computed, observable } from "mobx";
import FuelType from "entities/fuelType";
import EnumService from "services/enumService";
import LocalizationService from "services/localizationService";
import Vehicle from "../../models/vehicle";
import { startOfCzechDay } from "../../utils/dateUtils";
import CountryResponse from "entities/countryResponse";
import ChargeRegistrationsResponse from "entities/chargeRegistrationsResponse";
import RegisteredChargeResponse from "entities/registeredChargeResponse";
import ChargeTypesService from "services/chargeTypesService";
import FuelTypeResponse from "entities/fuelTypeResponse";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import ExemptionReason from "entities/exemptionReason";
import ChargeOrderService from "services/chargeOrderService";
import TimeService from "services/timeService";

export enum ChargeRegistrationStatus {
  valid = "valid",
  invalid = "invalid",
  exempted = "exempted",
  possibleExempted = "possibleExempted",
}

export const ValidationSummaryNavigationName = "summary";

@Router.registerRoute({ route: ValidationSummaryNavigationName, name: Router.Self })
export default class ValidationSummaryViewModel extends ScreenBase {
  private countRegex = /%COUNT%/g;
  private expirationRegex = /%EXPIRATION%/g;
  private validRegex = /%VALID%/g;
  @observable country?: CountryResponse;
  @observable queryParams: object;

  constructor(
    public vehicle: Vehicle,
    public chargeRegistrationsResponse: ChargeRegistrationsResponse,
    public localizationService: LocalizationService,
    public timeService: TimeService,
    private enumService: EnumService,
    private chargeTypesService: ChargeTypesService,
    private chargeOrderService: ChargeOrderService
  ) {
    super();
    this.navigationName = ValidationSummaryNavigationName;
  }

  protected async onInitialize(): Promise<Promise<any> | void> {
    this.getCountryEnum();
    await this.timeService.getCurrentServerTime();

    this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    return super.onInitialize();
  }

  async getCountryEnum() {
    const countryId = this.vehicle.countryId;
    if (countryId === czechID) {
      this.country = {
        id: czechID,
        name: "Česká republika",
        code: "CZ",
        isDeleted: false,
      };
    } else {
      this.country = (await this.enumService.lazyValue("countries", countryId!)) as CountryResponse;
    }
  }

  get navigationParams() {
    const countryId = this.vehicle.countryId;
    const licensePlate = this.vehicle.licensePlate;

    return countryId && licensePlate ? { countryId, licensePlate } : {};
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get status() {
    if (this.chargeRegistrationsResponse.isGivenExemption) {
      return ChargeRegistrationStatus.exempted;
    } else if (this.chargeRegistrationsResponse.charges.some(charge => charge.isCurrentlyValid)) {
      return ChargeRegistrationStatus.valid;
    } else if (this.chargeRegistrationsResponse.possibleExemptionReasonIds.length > 0) {
      return ChargeRegistrationStatus.possibleExempted;
    } else {
      return ChargeRegistrationStatus.invalid;
    }
  }

  @computed
  get possibleExemptionReason() {
    const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    return this.chargeRegistrationsResponse.possibleExemptionReasonIds
      ?.map(id => exemptionReasons.find(reason => reason.id === id)?.exemptionReason)
      .map(reason => {
        if (reason?.includes(ExemptionReason.O)) {
          return ExemptionReason.O;
        }
        return reason;
      })
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      });
  }

  @computed
  get validCharges(): RegisteredChargeResponse[] {
    return this.chargeRegistrationsResponse.charges.filter(charge => charge.isCurrentlyValid);
  }

  @computed
  get upcomingCharges(): RegisteredChargeResponse[] {
    return this.chargeRegistrationsResponse.charges.filter(charge => !charge.isCurrentlyValid);
  }

  @computed
  get hasExemption() {
    return this.chargeRegistrationsResponse.isGivenExemption;
  }

  @computed
  get buyEnabled() {
    return [ChargeRegistrationStatus.invalid, ChargeRegistrationStatus.possibleExempted].includes(this.status);
  }

  @computed
  get vehicleInfo() {
    const licensePlate = this.vehicle.licensePlate;

    if (!this.country?.code && !licensePlate) {
      return "";
    }

    return `${this.country?.code || "  "} – ${licensePlate}`;
  }

  @action.bound
  isChargeEco(charge: RegisteredChargeResponse) {
    const priceList = this.chargeTypesService.getPriceItemList(true);
    const priceListItem = priceList?.find(item => item.id === charge.priceListItemId);
    if (!priceListItem) {
      return false;
    }
    const fuelTypes = this.enumService.values("fuel_types") as FuelTypeResponse[];
    const fuelType = fuelTypes?.find(t => t.id.toString() === priceListItem.fuelTypeId)?.fuelType;
    if (!fuelType) {
      return false;
    }
    return [FuelType.NaturalGas, FuelType.Biomethane].includes(fuelType as FuelType);
  }

  @action.bound
  getChargeExpiration(charge: RegisteredChargeResponse) {
    const validDays = differenceInDays(charge.validUntil, new Date());

    if (validDays < 0 || new Date() < charge.validSince || new Date() > charge.validUntil) {
      return ""; // This can happen if the client has an incorrect date
    }

    let validDaysString;
    if (validDays === 0) {
      validDaysString = this.translate("today");
    } else {
      validDaysString = this.translate("days", validDays).replace(this.countRegex, validDays.toString());
    }

    return this.translate("expiration").replace(this.expirationRegex, validDaysString);
  }

  @action.bound
  getChargeValidIn(charge: RegisteredChargeResponse) {
    const validInDays = differenceInDays(charge.validSince, startOfCzechDay());

    if (validInDays < 0 || new Date() > charge.validSince) {
      return ""; // This can happen if the client has an incorrect date
    }

    let validInDaysString;
    if (validInDays === 0) {
      validInDaysString = this.translate("lessThanDay");
    } else {
      validInDaysString = this.translate("days", validInDays).replace(this.countRegex, validInDays.toString());
    }

    return this.translate("validIn").replace(this.validRegex, validInDaysString);
  }

  @action.bound
  chargeValidityRange(charge: RegisteredChargeResponse) {
    return charge.validUntil;
  }

  @action.bound
  next() {
    this.requestClose();
  }

  @action.bound
  buy() {
    this.chargeOrderService.createNewChargeOrder(this.vehicle);
    window.location.href = this.localizationService.translateGeneral("eshopRedirect", undefined, true);
  }

  @action.bound
  ta(code: string) {
    return this.localizationService.translateAttribute("chargeRegistration", code);
  }

  @action.bound
  translate(code: string, count: number | undefined = undefined, disabledUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`validation.${code}`, count, disabledUnbreakableRules);
  }

  static Factory({ container }: interfaces.Context) {
    return (vehicle: Vehicle, chargeRegistration: ChargeRegistrationsResponse) =>
      new ValidationSummaryViewModel(
        vehicle,
        chargeRegistration,
        container.get(LocalizationService),
        container.get(TimeService),
        container.get(EnumService),
        container.get(ChargeTypesService),
        container.get(ChargeOrderService)
      );
  }
}
